// src/pages/Questionnaire.jsx
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "../components/landing.css";
import { useNavigate } from "react-router-dom";

import { onAuthStateChanged } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, db, functions } from "../firebase";
import {
  collection,
  addDoc,
  doc,
  getDocs,
  query,
  setDoc,
  serverTimestamp,
  where,
} from "firebase/firestore";

import { scoreAssessment as scoreAssessmentOriginal } from "../utils/scoring";
import {
  hasCriticalPillar,
  DEFAULT_CRITICAL_THRESHOLD,
} from "../utils/criticalGuard";

function timestampToMillis(value) {
  if (!value) return 0;

  if (typeof value.toMillis === "function") {
    return value.toMillis();
  }

  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

function formatSavedDate(value) {
  const millis = timestampToMillis(value);

  if (!millis) return "an earlier session";

  return new Date(millis).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export default function Questionnaire({ questions = [], sector }) {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [assessmentId, setAssessmentId] = useState(null);

  const [active, setActive] = useState(0);

  // answers[qid] = { label: string, score: number (0–4) }
  const [answers, setAnswers] = useState({});

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCheckingDraft, setIsCheckingDraft] = useState(true);
  const [draftToResume, setDraftToResume] = useState(null);
  const [saveStatus, setSaveStatus] = useState("");

  const pillars = ["Environmental", "Social", "Governance"];

  const pageRef = useRef(null);
  const topRef = useRef(null);

  /*
    These refs prevent race conditions:
    - assessmentIdRef avoids creating two drafts if answers are selected quickly.
    - answersRef always contains the latest answers while Firestore saves are pending.
    - saveQueueRef ensures saves happen in order.
  */
  const assessmentIdRef = useRef(null);
  const draftCreationRef = useRef(null);
  const answersRef = useRef({});
  const saveQueueRef = useRef(Promise.resolve());

  const SCALE_OPTIONS = [
    { label: "Not in place", score: 0 },
    { label: "Informal / ad hoc", score: 1 },
    { label: "Partially structured", score: 2 },
    { label: "Implemented & documented", score: 3 },
    { label: "Advanced / best practice", score: 4 },
  ];

  function applyAssessmentId(id) {
    assessmentIdRef.current = id;
    setAssessmentId(id);
  }

  function applyAnswers(nextAnswers) {
    answersRef.current = nextAnswers;
    setAnswers(nextAnswers);
  }

  // ---------- AUTH ----------
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (authenticatedUser) => {
      setUser(authenticatedUser || null);
    });

    return () => unsub();
  }, []);

  // ---------- CANONICAL QUESTIONS ----------
  const canonicalQuestions = useMemo(() => {
    const byPillarCount = {
      Environmental: 0,
      Social: 0,
      Governance: 0,
    };

    return (questions || []).map((question) => {
      const pillar =
        question.category ||
        (question.pillar === "E"
          ? "Environmental"
          : question.pillar === "S"
          ? "Social"
          : question.pillar === "G"
          ? "Governance"
          : null);

      const index = pillar ? byPillarCount[pillar]++ : 0;
      const id =
        question.id || `${sector || "sector"}:${pillar || "pillar"}:${index}`;

      return {
        ...question,
        id,
        text: question.question || question.text || "",
        __pillar: pillar,
      };
    });
  }, [questions, sector]);

  // ---------- GROUPED UI MODEL ----------
  const grouped = useMemo(() => {
    const byPillar = {
      Environmental: [],
      Social: [],
      Governance: [],
    };

    canonicalQuestions.forEach((question) => {
      const pillar = question.__pillar;

      if (!pillar || !byPillar[pillar]) return;

      byPillar[pillar].push({
        ...question,
        qid: question.id,
        pillar,
      });
    });

    return byPillar;
  }, [canonicalQuestions]);

  const currentKey = pillars[active];
  const currentQs = grouped[currentKey] || [];

  const progressFor = (key) => {
    const list = grouped[key] || [];
    const total = list.length;

    if (!total) return 0;

    const completed = list.filter(
      (question) =>
        answers[question.qid] &&
        typeof answers[question.qid].score === "number"
    ).length;

    return Math.round((completed / total) * 100);
  };

  const canProceed = () =>
    currentQs.every(
      (question) =>
        answers[question.qid] &&
        typeof answers[question.qid].score === "number"
    );

  /*
    This fixes an existing weakness:
    users must complete every question before submitting,
    even if they manually click directly on the Governance tab.
  */
  const allQuestionsAnswered = () =>
    canonicalQuestions.every(
      (question) =>
        answers[question.id] &&
        typeof answers[question.id].score === "number"
    );

  // ---------- FIND A PREVIOUS UNSUBMITTED DRAFT ----------
  useEffect(() => {
    let cancelled = false;

    async function findSavedDraft() {
      setIsCheckingDraft(true);
      setDraftToResume(null);
      setSaveStatus("");

      applyAssessmentId(null);
      applyAnswers({});
      setActive(0);

      if (!user || !sector) {
        if (!cancelled) {
          setIsCheckingDraft(false);
        }
        return;
      }

      try {
        const assessmentsRef = collection(
          db,
          "users",
          user.uid,
          "assessments"
        );

        /*
          We retrieve draft assessments and then filter by sector locally.
          This avoids requiring a compound Firestore index just for this feature.
        */
        const draftQuery = query(
          assessmentsRef,
          where("status", "==", "draft")
        );

        const snapshot = await getDocs(draftQuery);

        const availableDrafts = snapshot.docs
          .map((snapshotDoc) => ({
            id: snapshotDoc.id,
            ...snapshotDoc.data(),
          }))
          .filter(
            (draft) =>
              draft.sector === sector &&
              draft.answers &&
              Object.keys(draft.answers).length > 0
          )
          .sort(
            (first, second) =>
              timestampToMillis(second.updatedAt) -
              timestampToMillis(first.updatedAt)
          );

        if (!cancelled && availableDrafts.length > 0) {
          setDraftToResume(availableDrafts[0]);
        }
      } catch (error) {
        console.error("[draft restore] failed to retrieve saved draft:", error);
        setSaveStatus(
          "Saved progress could not be checked. You can still start the assessment."
        );
      } finally {
        if (!cancelled) {
          setIsCheckingDraft(false);
        }
      }
    }

    findSavedDraft();

    return () => {
      cancelled = true;
    };
  }, [user, sector]);

  // ---------- CREATE A DRAFT ONLY AFTER THE USER ANSWERS ----------
  const ensureDraft = async () => {
    if (!user) {
      throw new Error("No authenticated user");
    }

    if (assessmentIdRef.current) {
      return assessmentIdRef.current;
    }

    if (draftCreationRef.current) {
      return draftCreationRef.current;
    }

    draftCreationRef.current = (async () => {
      const assessmentsRef = collection(
        db,
        "users",
        user.uid,
        "assessments"
      );

      const createdDraft = await addDoc(assessmentsRef, {
        sector,
        status: "draft",
        answers: {},
        activePillar: active,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      applyAssessmentId(createdDraft.id);
      return createdDraft.id;
    })();

    try {
      return await draftCreationRef.current;
    } finally {
      draftCreationRef.current = null;
    }
  };

  // ---------- SAVE PROGRESS ----------
  const persistProgress = async (nextAnswers, nextActivePillar) => {
    if (!user) return;

    try {
      const id = await ensureDraft();
      const draftRef = doc(db, "users", user.uid, "assessments", id);

      await setDoc(
        draftRef,
        {
          sector,
          status: "draft",
          answers: nextAnswers,
          activePillar: nextActivePillar,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      /*
        Only show "Progress saved" if this save still represents
        the most recent answer set.
      */
      if (answersRef.current === nextAnswers) {
        setSaveStatus("Progress saved");
      }
    } catch (error) {
      console.error("[draft save] failed:", error);
      setSaveStatus(
        "Saving failed. Your latest answer may not have been stored."
      );
    }
  };

  const queueProgressSave = (nextAnswers, nextActivePillar) => {
    setSaveStatus("Saving…");

    saveQueueRef.current = saveQueueRef.current
      .catch(() => undefined)
      .then(() => persistProgress(nextAnswers, nextActivePillar));

    return saveQueueRef.current;
  };

  // ---------- ANSWER HANDLER ----------
  const handleAnswer = (qid, option) => {
    const updatedAnswers = {
      ...answersRef.current,
      [qid]: {
        label: option.label,
        score: option.score,
      },
    };

    applyAnswers(updatedAnswers);
    queueProgressSave(updatedAnswers, active);
  };

  // ---------- PILLAR NAVIGATION ----------
  const changePillar = (nextActive) => {
    const boundedIndex = Math.min(
      pillars.length - 1,
      Math.max(0, nextActive)
    );

    setActive(boundedIndex);

    if (
      assessmentIdRef.current &&
      Object.keys(answersRef.current).length > 0
    ) {
      queueProgressSave(answersRef.current, boundedIndex);
    }
  };

  const goPrev = () => changePillar(active - 1);
  const goNext = () => changePillar(active + 1);

  // ---------- RESUME OR DISCARD SAVED PROGRESS ----------
  const resumeSavedDraft = () => {
    if (!draftToResume) return;

    const restoredAnswers = draftToResume.answers || {};
    const restoredPillar =
      typeof draftToResume.activePillar === "number"
        ? Math.min(
            pillars.length - 1,
            Math.max(0, draftToResume.activePillar)
          )
        : 0;

    applyAssessmentId(draftToResume.id);
    applyAnswers(restoredAnswers);
    setActive(restoredPillar);
    setDraftToResume(null);
    setSaveStatus("Saved progress restored");
  };

  const discardSavedDraftAndStartNew = async () => {
    if (!draftToResume || !user) return;

    const confirmed = window.confirm(
      "Discard your saved unfinished assessment and start a new one?"
    );

    if (!confirmed) return;

    try {
      const oldDraftRef = doc(
        db,
        "users",
        user.uid,
        "assessments",
        draftToResume.id
      );

      await setDoc(
        oldDraftRef,
        {
          status: "abandoned",
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      applyAssessmentId(null);
      applyAnswers({});
      setActive(0);
      setDraftToResume(null);
      setSaveStatus("");
    } catch (error) {
      console.error("[draft discard] failed:", error);
      alert(
        "The saved draft could not be discarded. Please try again before starting a new assessment."
      );
    }
  };

  // ---------- SCORING ----------
  const safeScoreAssessment = (answersToScore = answersRef.current) => {
    const clamp = (number, min, max) =>
      Math.min(max, Math.max(min, number));

    try {
      const result = scoreAssessmentOriginal(
        canonicalQuestions,
        answersToScore,
        {
          sector,
          allowPartial: false,
          treatUnknownAsZero: false,
        }
      );

      if (!result || typeof result !== "object") {
        throw new Error("No scorer result.");
      }

      const scoredPillars = result.pillars || {};
      const overall100Raw = result.overall;

      if (
        typeof scoredPillars.E !== "number" ||
        typeof scoredPillars.S !== "number" ||
        typeof scoredPillars.G !== "number"
      ) {
        throw new Error("Invalid pillars.");
      }

      if (
        typeof overall100Raw !== "number" ||
        Number.isNaN(overall100Raw)
      ) {
        throw new Error("Invalid overall.");
      }

      const pillars100 = {
        E: clamp(scoredPillars.E, 0, 100),
        S: clamp(scoredPillars.S, 0, 100),
        G: clamp(scoredPillars.G, 0, 100),
      };

      const overall100 = clamp(overall100Raw, 0, 100);

      const pillarScores01 = {
        E: pillars100.E / 100,
        S: pillars100.S / 100,
        G: pillars100.G / 100,
      };

      return {
        pillars100,
        overall100,
        pillarScores01,
        overall01: overall100 / 100,
        rating: result.rating ?? null,
      };
    } catch (error) {
      console.warn("[scoreAssessment] failed, using fallback:", error);

      const scorePillarPct = (key) => {
        const list = grouped[key] || [];

        if (!list.length) return 0;

        let total = 0;
        let count = 0;

        list.forEach((question) => {
          const answer = answersToScore[question.qid];

          if (!answer) return;

          let value = 0;

          if (
            typeof answer === "object" &&
            typeof answer.score === "number"
          ) {
            value = answer.score;
          }

          value = clamp(value, 0, 4);
          total += value;
          count += 1;
        });

        if (!count) return 0;

        return Math.round((total / (count * 4)) * 100);
      };

      const environmental100 = scorePillarPct("Environmental");
      const social100 = scorePillarPct("Social");
      const governance100 = scorePillarPct("Governance");

      const overall100 = Math.round(
        (environmental100 + social100 + governance100) / 3
      );

      return {
        pillars100: {
          E: environmental100,
          S: social100,
          G: governance100,
        },
        overall100,
        pillarScores01: {
          E: environmental100 / 100,
          S: social100 / 100,
          G: governance100 / 100,
        },
        overall01: overall100 / 100,
        rating: null,
      };
    }
  };

  // ---------- SUBMIT ----------
  const handleSubmitAll = async () => {
    if (!user || !allQuestionsAnswered()) return;

    setIsSubmitting(true);

    try {
      /*
        Finish any queued autosave before converting the draft to submitted.
        Otherwise a delayed draft save could overwrite the submitted status.
      */
      await saveQueueRef.current.catch(() => undefined);

      const id = await ensureDraft();
      const finalAnswers = answersRef.current;

      const {
        pillars100,
        overall100,
        pillarScores01,
        overall01,
        rating,
      } = safeScoreAssessment(finalAnswers);

      const critical = hasCriticalPillar(
        pillarScores01,
        DEFAULT_CRITICAL_THRESHOLD
      );

      const assessmentRef = doc(
        db,
        "users",
        user.uid,
        "assessments",
        id
      );

      const payload = {
        answers: finalAnswers,
        sector,
        status: "submitted",
        activePillar: active,
        updatedAt: serverTimestamp(),
        completedAt: serverTimestamp(),

        overallScore: overall100,
        envScore: pillars100.E,
        socScore: pillars100.S,
        govScore: pillars100.G,
        rating: rating ?? null,

        pillarScores: pillarScores01,
        overall: overall01,

        critical,
        threshold: DEFAULT_CRITICAL_THRESHOLD,
      };

      await setDoc(assessmentRef, payload, { merge: true });

      try {
        const userRef = doc(db, "users", user.uid);

        await setDoc(
          userRef,
          {
            lastAssessmentAt: serverTimestamp(),
          },
          { merge: true }
        );
      } catch (error) {
        console.error("[submit] failed to update lastAssessmentAt:", error);
      }

      if (critical) {
        try {
          const sendCriticalAlertEmail = httpsCallable(
            functions,
            "sendCriticalAlertEmail"
          );

          await sendCriticalAlertEmail({
            user: {
              uid: auth.currentUser?.uid || null,
              email: auth.currentUser?.email || null,
              displayName: auth.currentUser?.displayName || null,
            },
            profile: { sector },
            scores: {
              ...pillarScores01,
              overall: overall01,
            },
            scores100: {
              ...pillars100,
              overall: overall100,
            },
            threshold: DEFAULT_CRITICAL_THRESHOLD,
            source: "questionnaire-submit",
            assessmentId: id,
          });
        } catch (error) {
          console.error(
            "[submit] sendCriticalAlertEmail failed (non-blocking):",
            error
          );
        }

        navigate("/critical", { replace: true });
        return;
      }

      navigate("/dashboard", {
        state: {
          assessmentId: id,
          sector,
        },
        replace: true,
      });
    } catch (error) {
      console.error("[submit] unexpected error:", error);
      alert("Submit failed. Open the console for details.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ---------- SCROLL TO TOP WHEN CHANGING PILLAR ----------
  useLayoutEffect(() => {
    if (pageRef.current && typeof pageRef.current.scrollTo === "function") {
      pageRef.current.scrollTo({
        top: 0,
        left: 0,
        behavior: "auto",
      });
    }

    const scrollingElement =
      document.scrollingElement || document.documentElement;

    if (scrollingElement) {
      scrollingElement.scrollTop = 0;
    }

    if (topRef.current) {
      topRef.current.scrollIntoView({
        behavior: "auto",
        block: "start",
      });
    }
  }, [active]);

  if (!user) {
    return <div style={{ padding: 24 }}>Devi effettuare il login.</div>;
  }

  if (isCheckingDraft) {
    return (
      <div
        style={{
          display: "grid",
          placeItems: "center",
          height: "60vh",
          color: "#475569",
        }}
      >
        Checking saved progress…
      </div>
    );
  }

  // ---------- UI ----------
  return (
    <div ref={pageRef} className="landing" style={{ alignItems: "center" }}>
      <main className="landing__main" style={{ maxWidth: 960 }}>
        <div ref={topRef} />

        <h1 className="landing__title">ESG Assessment</h1>
        <p className="landing__subtitle">Sector: {sector}</p>

        {saveStatus && (
          <p
            aria-live="polite"
            style={{
              marginTop: 10,
              marginBottom: 0,
              color: saveStatus.includes("failed") ? "#b91c1c" : "#148A58",
              fontSize: 13,
            }}
          >
            {saveStatus}
          </p>
        )}

        {/* Tabs */}
        <div
          style={{
            display: "flex",
            gap: 6,
            margin: "16px 0",
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
            padding: "0 8px",
          }}
        >
          {pillars.map((pillar, index) => (
            <button
              key={pillar}
              className={`btn ${
                index === active ? "btn--primary" : "btn--ghost"
              }`}
              onClick={() => changePillar(index)}
              disabled={isSubmitting || !!draftToResume}
              type="button"
              style={{
                flex: "0 0 auto",
                padding: "8px 10px",
                fontSize: 13,
                whiteSpace: "nowrap",
              }}
            >
              {pillar}
              <span style={{ marginLeft: 6, opacity: 0.75 }}>
                {progressFor(pillar)}%
              </span>
            </button>
          ))}
        </div>

        {/* Card */}
        <section
          className="card"
          style={{
            padding: 16,
            borderRadius: 16,
            border: "1px solid #e2e8f0",
          }}
        >
          <h2 className="landing__subtitle" style={{ marginBottom: 8 }}>
            {currentKey}
          </h2>

          <div
            style={{
              height: 6,
              background: "#e2e8f0",
              borderRadius: 999,
              margin: "8px 0 16px",
            }}
          >
            <div
              style={{
                width: `${progressFor(currentKey)}%`,
                height: "100%",
                borderRadius: 999,
                background: "#111827",
              }}
            />
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: 12,
            }}
          >
            {currentQs.map((question) => (
              <div
                key={question.qid}
                className="question-row"
                style={{
                  padding: 12,
                  borderRadius: 12,
                  border: "1px solid #eef2f7",
                }}
              >
                <label
                  style={{
                    display: "block",
                    fontSize: 14,
                    color: "#334155",
                    marginBottom: 10,
                  }}
                >
                  {question.text}
                </label>

                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    flexWrap: "wrap",
                  }}
                >
                  {SCALE_OPTIONS.map((option) => {
                    const selected =
                      answers[question.qid]?.score === option.score;

                    return (
                      <button
                        key={option.score}
                        type="button"
                        className={`btn ${
                          selected ? "btn--primary" : "btn--ghost"
                        }`}
                        onClick={() =>
                          handleAnswer(question.qid, option)
                        }
                        disabled={isSubmitting || !!draftToResume}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

     <div
  style={{
    display: "flex",
    gap: 8,
    marginTop: 16,
    flexWrap: "wrap",
    alignItems: "center",
  }}
>
  <button
    className="btn btn--ghost"
    onClick={async () => {
      try {
        if (Object.keys(answersRef.current).length > 0) {
          await queueProgressSave(answersRef.current, active);
        }

        await saveQueueRef.current.catch(() => undefined);
      } catch (error) {
        console.error("[return to dashboard] save failed:", error);
      } finally {
        navigate("/dashboard", { replace: true });
      }
    }}
    disabled={isSubmitting || !!draftToResume}
    type="button"
  >
    Return to dashboard
  </button>

  {active > 0 && (
    <button
      className="btn btn--ghost"
      onClick={goPrev}
      disabled={isSubmitting || !!draftToResume}
      type="button"
    >
      Back
    </button>
  )}

  <div style={{ flex: 1 }} />

  {active < pillars.length - 1 ? (
    <button
      className="btn btn--primary"
      onClick={goNext}
      disabled={!canProceed() || isSubmitting || !!draftToResume}
      type="button"
    >
      Next
    </button>
  ) : (
    <button
      className="btn btn--primary"
      onClick={handleSubmitAll}
      disabled={
        !allQuestionsAnswered() ||
        isSubmitting ||
        !!draftToResume
      }
      type="button"
    >
      {isSubmitting ? "Submitting..." : "Show results"}
    </button>
  )}
</div>
        </section>
      </main>

      {draftToResume && (
        <div style={overlayStyle}>
          <div
            style={modalStyle}
            role="dialog"
            aria-modal="true"
            aria-labelledby="saved-progress-title"
          >
            <h2 id="saved-progress-title" style={modalTitleStyle}>
              Saved assessment found
            </h2>

            <p style={modalTextStyle}>
              You have unfinished progress for the{" "}
              <strong>{sector}</strong> assessment, last saved on{" "}
              <strong>{formatSavedDate(draftToResume.updatedAt)}</strong>.
            </p>

            <p style={modalTextStyle}>
              Would you like to resume your saved assessment or start again?
            </p>

            <div style={modalButtonRowStyle}>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={discardSavedDraftAndStartNew}
              >
                Start new
              </button>

              <button
                type="button"
                className="btn btn--primary"
                onClick={resumeSavedDraft}
                autoFocus
              >
                Resume assessment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(15, 23, 42, 0.48)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: 20,
  zIndex: 9999,
};

const modalStyle = {
  width: "100%",
  maxWidth: 470,
  background: "#ffffff",
  borderRadius: 18,
  padding: "28px 26px",
  boxShadow: "0 20px 55px rgba(15, 23, 42, 0.18)",
};

const modalTitleStyle = {
  margin: "0 0 12px",
  color: "#111827",
  fontSize: 22,
  lineHeight: 1.25,
};

const modalTextStyle = {
  margin: "0 0 12px",
  color: "#374151",
  fontSize: 15,
  lineHeight: 1.6,
};

const modalButtonRowStyle = {
  display: "flex",
  gap: 10,
  justifyContent: "flex-end",
  flexWrap: "wrap",
  marginTop: 22,
};






